import React, { useState, useEffect } from 'react';
import { cmsNavLinks } from '@/lib/pagePaths';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  GraduationCap,
  Menu,
  X,
  User,
  LogOut,
  Settings,
  BookOpen,
  LayoutDashboard,
  Award,
  Type,
  FileText,
} from 'lucide-react';
import axios from 'axios';
import { useTextSize } from '@/contexts/TextSizeContext';

import { BACKEND_URL } from '@/config';
const API = `${BACKEND_URL}/api`;

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { size, changeSize, sizes } = useTextSize();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cmsNav, setCmsNav] = useState([]);
  const [cmsSettings, setCmsSettings] = useState(null);

  useEffect(() => {
    fetchCmsData();
  }, []);

  const fetchCmsData = async () => {
    try {
      const [navRes, settingsRes] = await Promise.all([
        axios.get(`${API}/cms/pages/navigation`),
        axios.get(`${API}/cms/settings`),
      ]);
      setCmsNav(navRes.data.header || []);
      setCmsSettings(settingsRes.data);

      // Dynamically set favicon if uploaded
      const faviconUrl = settingsRes.data?.favicon_url;
      if (faviconUrl) {
        const resolvedUrl = faviconUrl.startsWith('/api/') ? `${BACKEND_URL}${faviconUrl}` : faviconUrl;
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = resolvedUrl;
      }
    } catch (e) {
      // Fallback silently
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/dashboard';
    switch (user.role) {
      case 'super_admin': return '/admin';
      case 'content_manager': return '/cms';
      case 'org_admin': return '/organisation';
      default: return '/dashboard';
    }
  };

  const isActive = (path) => location.pathname === path;
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const defaultLinks = [
    { label: 'Courses', url: '/courses' },
    { label: 'Knowledge Hub', url: '/knowledge-hub' },
    { label: 'Pricing', url: '/pricing' },
  ];

  const navLinks = cmsNav.length > 0
    ? [...defaultLinks, ...cmsNavLinks(cmsNav)]
    : [...defaultLinks, { label: 'About Us', url: '/about' }, { label: 'For Organisations', url: '/for-organisations' }];

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#cbd5e1]/30" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-6 h-16 lg:h-[76px]">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 h-full py-2" data-testid="logo-link">
            {cmsSettings?.logo_url ? (
              <img
                src={cmsSettings.logo_url.startsWith('/api/') ? `${BACKEND_URL}${cmsSettings.logo_url}` : cmsSettings.logo_url}
                alt={cmsSettings?.logo_alt_text || 'Common Impact E-Learning'}
                className="block h-10 lg:h-12 w-auto max-w-[170px] lg:max-w-[210px] object-contain object-left"
                style={{ background: 'transparent' }}
              />
            ) : (
              <img
                src="/logo-header.png"
                alt="Common Impact E-Learning"
                className="block h-10 lg:h-12 w-auto max-w-[170px] lg:max-w-[210px] object-contain object-left"
                style={{ background: 'transparent' }}
              />
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex flex-1 items-center justify-center gap-1 2xl:gap-2" aria-label="Main navigation" data-testid="desktop-nav">
            {navLinks.map((link, idx) => (
              <Link
                key={idx}
                to={link.url}
                className={`inline-flex items-center h-10 px-3 xl:px-3.5 rounded-full text-sm font-medium leading-none whitespace-nowrap transition-colors duration-200 ${
                  isActive(link.url)
                    ? 'text-[#20acf6] bg-[#20acf6]/10'
                    : 'text-[#1e293b]/75 hover:text-[#20acf6] hover:bg-[#f1f5f9]'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Auth Actions */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 hover:bg-[#f8fafc]"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 bg-[#20acf6] rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                      </span>
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-[#1e293b]">
                      {user?.first_name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-medium">{user?.first_name} {user?.last_name}</span>
                      <span className="text-xs text-[#1e293b]/50">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="menu-dashboard">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-courses')} data-testid="menu-my-courses">
                    <BookOpen className="mr-2 h-4 w-4" />
                    My Courses
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/certificates')} data-testid="menu-certificates">
                    <Award className="mr-2 h-4 w-4" />
                    Certificates
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/transcript')} data-testid="menu-transcript">
                    <FileText className="mr-2 h-4 w-4" />
                    Learning Record
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  {user?.role === 'super_admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/settings')} data-testid="menu-settings">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-[#1e293b]/50 flex items-center gap-1.5">
                    <Type className="h-3 w-3" /> Text Size
                  </DropdownMenuLabel>
                  <div className="px-2 pb-1.5 flex gap-1" data-testid="text-size-toggle">
                    {Object.entries(sizes).map(([key, s]) => (
                      <button
                        key={key}
                        onClick={() => changeSize(key)}
                        className={`flex-1 text-xs py-1 rounded-md transition-colors ${size === key ? 'bg-[#20acf6] text-white font-medium' : 'bg-[#f8fafc] text-[#1e293b]/70 hover:bg-slate-200'}`}
                        data-testid={`text-size-${key}`}
                      >
                        {key === 'xl' ? 'XL' : s.label.charAt(0)}
                      </button>
                    ))}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="hidden sm:inline-flex items-center h-10 px-4 rounded-full text-sm font-medium text-[#1e293b] hover:bg-[#f1f5f9]"
                  data-testid="login-btn"
                >
                  Log in
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="inline-flex items-center h-10 px-5 rounded-full text-sm font-semibold bg-[#20acf6] hover:bg-[#1b9ad9] text-white shadow-[0_6px_16px_-8px_rgba(32,172,246,0.9)] transition-colors"
                  data-testid="register-btn"
                >
                  Sign up
                </Button>
              </div>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="xl:hidden h-10 w-10 rounded-full hover:bg-[#f1f5f9]"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="xl:hidden py-3 border-t border-slate-100 max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-nav">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link, idx) => (
                <Link
                  key={idx}
                  to={link.url}
                  className={`px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive(link.url) ? 'text-[#20acf6] bg-[#20acf6]/10' : 'text-[#1e293b]/75 hover:text-[#20acf6] hover:bg-[#f1f5f9]'}`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
              {!isAuthenticated && (
                <Link
                  to="/login"
                  className="px-3 py-2.5 text-sm font-medium text-[#20acf6] hover:bg-[#f8fafc] rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Log in
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
